/**
 * Admin → Time clock (v2.3.6).
 *
 *  1. OVERRIDE APPROVAL QUEUE — every employee time override lands here. It
 *     shows the SYSTEM value, the REQUESTED value and the REASON. Nothing the
 *     employee typed takes effect until an admin approves it; only the APPROVED
 *     value flows to ERPNext.
 *  2. HOLIDAY CALENDAR — the dates that make work ON CALL. Admin CRUD.
 *
 * ADMIN ONLY. Hiding the UI is not the boundary: the server returns 403 to any
 * non-admin token on /api/timeclock/pending, /override/{id}/approve|reject and
 * every holiday write.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Stack,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Refresh as RefreshIcon,
  Lock as LockIcon, Add as AddIcon, Delete as DeleteIcon,
  EventBusy as HolidayIcon, PhoneInTalk as OnCallIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Override {
  id: number;
  user_id: number;
  employee: string;
  employee_email?: string;
  work_date: string;
  field: string;
  system_value: string | null;
  requested_value: string;
  reason_code: string;
  reason_label: string;
  reason_text?: string | null;
  status: string;
  requested_at: string;
  is_on_call: boolean;
  on_call_reason?: string | null;
  day_status: string;
}

interface Holiday {
  id: number;
  holiday_date: string;
  name: string;
  scheduled_off: boolean;
}

const FIELD_LABEL: Record<string, string> = {
  clock_in: 'Clock in',
  clock_out: 'Clock out',
  worked_minutes: 'Hours worked',
  break_minutes: 'Break',
  day_type: 'Day type',
};

const fmt = (v: string | null) => {
  if (!v) return <em style={{ opacity: 0.6 }}>none recorded</em>;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    const d = new Date(v.replace(' ', 'T'));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }
  }
  return v;
};

export default function TimeclockAdmin() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.is_superadmin
    || (user.roles || []).some((r: any) => ['Admin', 'SuperAdmin'].includes(r?.name || r)));

  const [pending, setPending] = useState<Override[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [oncallRules, setOncallRules] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ t: 'success' | 'error'; m: string } | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const [dlg, setDlg] = useState<{ ov: Override; action: 'approve' | 'reject' } | null>(null);
  const [note, setNote] = useState('');
  const [editedValue, setEditedValue] = useState('');

  const [newHol, setNewHol] = useState({ holiday_date: '', name: '', scheduled_off: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get('/timeclock/pending'),
        api.get('/timeclock/holidays'),
      ]);
      setPending(p.data.overrides || []);
      setHolidays(h.data.holidays || []);
      setOncallRules(h.data.oncall_rules || null);
    } catch (e: any) {
      setMsg({ t: 'error', m: e?.response?.data?.detail || 'Could not load the time-clock queue.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = async () => {
    if (!dlg) return;
    setBusy(dlg.ov.id);
    try {
      const body: any = { review_note: note || undefined };
      if (dlg.action === 'approve' && editedValue && editedValue !== dlg.ov.requested_value) {
        body.approved_value = editedValue;
      }
      const r = await api.post(`/timeclock/override/${dlg.ov.id}/${dlg.action}`, body);
      const day = r.data?.day;
      const erp = day?.erp_timesheet_no;
      setMsg({
        t: 'success',
        m: dlg.action === 'approve'
          ? `Approved. The approved value is now the one that flows to ERP.${erp ? ` ERPNext Timesheet ${erp} created.` : ''}`
          : 'Rejected. The system value stands; nothing changed.',
      });
      setDlg(null); setNote(''); setEditedValue('');
      await load();
    } catch (e: any) {
      setMsg({ t: 'error', m: e?.response?.data?.detail || 'That did not go through.' });
    } finally {
      setBusy(null);
    }
  };

  const addHoliday = async () => {
    if (!newHol.holiday_date || !newHol.name.trim()) {
      setMsg({ t: 'error', m: 'A holiday needs a date and a name.' });
      return;
    }
    try {
      await api.post('/timeclock/holidays', newHol);
      setNewHol({ holiday_date: '', name: '', scheduled_off: true });
      setMsg({ t: 'success', m: 'Holiday added. Work on that day is now classified ON CALL.' });
      await load();
    } catch (e: any) {
      setMsg({ t: 'error', m: e?.response?.data?.detail || 'Could not add the holiday.' });
    }
  };

  const delHoliday = async (id: number) => {
    try {
      await api.delete(`/timeclock/holidays/${id}`);
      await load();
    } catch (e: any) {
      setMsg({ t: 'error', m: e?.response?.data?.detail || 'Could not remove the holiday.' });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box data-testid="timeclock-admin">
      {!isAdmin && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
          <strong>Read-only.</strong> Only an Admin can approve time overrides or edit the holiday
          calendar. The server rejects the write (403) even if the controls were enabled.
        </Alert>
      )}
      {msg && <Alert severity={msg.t} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.m}</Alert>}

      {/* ---------------- OVERRIDE APPROVAL QUEUE ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="override-queue">
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Time override approvals
              <Chip
                size="small"
                color={pending.length ? 'warning' : 'success'}
                label={pending.length ? `${pending.length} waiting` : 'nothing waiting'}
                sx={{ ml: 1.5 }}
              />
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => void load()}>
              Refresh
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            An employee verifies their time every work day. When they disagree with what the
            tracker recorded they file an override <strong>with a reason</strong>. It changes
            nothing until you approve it here — and <strong>only the approved value flows to
            ERPNext</strong>. The original system value is kept forever.
          </Typography>

          {pending.length === 0 ? (
            <Alert severity="success">No time overrides are waiting for approval.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Day</strong></TableCell>
                  <TableCell><strong>Field</strong></TableCell>
                  <TableCell><strong>System recorded</strong></TableCell>
                  <TableCell><strong>Employee says</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell align="right"><strong>Decision</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      {o.employee}
                      {o.is_on_call && (
                        <Tooltip title={`On call — ${o.on_call_reason}`}>
                          <Chip size="small" color="secondary" icon={<OnCallIcon />}
                            label="ON CALL" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{o.work_date}</TableCell>
                    <TableCell>{FIELD_LABEL[o.field] || o.field}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{fmt(o.system_value)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{fmt(o.requested_value)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={o.reason_label} />
                      {o.reason_text && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          “{o.reason_text}”
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={<ApproveIcon />} disabled={!isAdmin || busy === o.id}
                          onClick={() => { setDlg({ ov: o, action: 'approve' }); setEditedValue(o.requested_value); setNote(''); }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small" variant="outlined" color="error"
                          startIcon={<RejectIcon />} disabled={!isAdmin || busy === o.id}
                          onClick={() => { setDlg({ ov: o, action: 'reject' }); setNote(''); }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ---------------- HOLIDAY CALENDAR ---------------- */}
      <Card data-testid="holiday-card">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            <HolidayIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Holiday calendar
            <Chip size="small" color="secondary" label="drives ON CALL" sx={{ ml: 1.5 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Work performed on a <strong>scheduled-off</strong> holiday is automatically classified
            <strong> ON CALL</strong> — the labor bills on the <code>ON-CALL</code> item in ERPNext
            instead of <code>LABOR-STANDARD</code>.
            {oncallRules && (
              <>
                {' '}Currently on-call also covers:{' '}
                <strong>{(oncallRules.days || []).join(', ').toUpperCase() || 'no weekend days'}</strong>
                {oncallRules.after_clockout ? ', and any work after clock-out' : ''}. The days,
                the rate and the multiplier live in <strong>Admin → Billing Rules</strong>.
              </>
            )}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Date" type="date" size="small" disabled={!isAdmin}
              InputLabelProps={{ shrink: true }}
              value={newHol.holiday_date}
              onChange={(e) => setNewHol({ ...newHol, holiday_date: e.target.value })}
              inputProps={{ 'data-testid': 'holiday-date' }}
            />
            <TextField
              label="Holiday name" size="small" fullWidth disabled={!isAdmin}
              value={newHol.name}
              onChange={(e) => setNewHol({ ...newHol, name: e.target.value })}
              inputProps={{ 'data-testid': 'holiday-name' }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={newHol.scheduled_off} disabled={!isAdmin}
                  onChange={(e) => setNewHol({ ...newHol, scheduled_off: e.target.checked })}
                />
              )}
              label="Scheduled off"
            />
            <Button
              variant="contained" startIcon={<AddIcon />} disabled={!isAdmin}
              onClick={() => void addHoliday()} sx={{ whiteSpace: 'nowrap' }}
              data-testid="holiday-add"
            >
              Add
            </Button>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Holiday</strong></TableCell>
                <TableCell><strong>Scheduled off</strong></TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell>{h.holiday_date}</TableCell>
                  <TableCell>{h.name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={h.scheduled_off ? 'secondary' : 'default'}
                      label={h.scheduled_off ? 'Yes — on call' : 'No — normal day'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small" color="error" startIcon={<DeleteIcon />} disabled={!isAdmin}
                      onClick={() => void delHoliday(h.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {holidays.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No holidays yet — add one above.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ---------------- decision dialog ---------------- */}
      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dlg?.action === 'approve' ? 'Approve this time override' : 'Reject this time override'}
        </DialogTitle>
        <DialogContent>
          {dlg && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity={dlg.action === 'approve' ? 'success' : 'warning'}>
                {dlg.action === 'approve'
                  ? 'The approved value becomes the one that flows to ERPNext. The original system value is kept as the audit record.'
                  : 'Rejecting leaves the system value standing. Nothing is changed and nothing new goes to ERP.'}
              </Alert>
              <Typography variant="body2">
                <strong>{dlg.ov.employee}</strong> · {dlg.ov.work_date} ·{' '}
                {FIELD_LABEL[dlg.ov.field] || dlg.ov.field}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System recorded: <strong>{dlg.ov.system_value || 'nothing'}</strong><br />
                Employee says: <strong>{dlg.ov.requested_value}</strong><br />
                Reason: <strong>{dlg.ov.reason_label}</strong>
                {dlg.ov.reason_text ? ` — “${dlg.ov.reason_text}”` : ''}
              </Typography>
              {dlg.action === 'approve' && (
                <TextField
                  label="Value you are approving (you may correct it)"
                  size="small" fullWidth value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                />
              )}
              <TextField
                label="Note (optional — kept on the audit record and on the ERP timesheet)"
                size="small" fullWidth multiline rows={2}
                value={note} onChange={(e) => setNote(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={dlg?.action === 'approve' ? 'success' : 'error'}
            onClick={() => void decide()}
            disabled={busy !== null}
            data-testid="confirm-decision"
          >
            {dlg?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
